// OAuth 2.1 consent page for remote MCP connectors (claude.ai).
// The API's /oauth/authorize endpoint validates the client and redirect_uri
// before sending the browser here, and /oauth/consent/approve re-validates
// them server-side. What the user is shown about the client (name, redirect
// host) comes from GET /oauth/clients/:id — never from this page's query
// string, which anyone can craft (e.g. client_name=Claude). The redirect_uri
// and scope params that the page does read are checked by lib/oauthConsent.js.
import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { AlertTriangle, ShieldCheck, X } from 'lucide-react';
import { useAuth } from '../context/AuthContext.jsx';
import { apiFetch } from '../services/apiClient.js';
import { SCOPE_DESCRIPTIONS, displayScopes, parseSafeRedirectUri } from '../lib/oauthConsent.js';
import styles from './OAuthConsent.module.css';

export default function OAuthConsent() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, loading: authLoading } = useAuth();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [client, setClient] = useState(null);
  const [clientError, setClientError] = useState(false);

  const clientId = searchParams.get('client_id');
  const redirectUri = searchParams.get('redirect_uri');
  const scopes = displayScopes(searchParams.get('scope'));

  useEffect(() => {
    if (!clientId) return;
    let cancelled = false;
    apiFetch(`/oauth/clients/${encodeURIComponent(clientId)}`)
      .then((data) => { if (!cancelled) setClient(data); })
      .catch(() => { if (!cancelled) setClientError(true); });
    return () => { cancelled = true; };
  }, [clientId]);

  if (!authLoading && !user) {
    const back = `/oauth/consent?${searchParams.toString()}`;
    navigate(`/auth?redirect=${encodeURIComponent(back)}`, { replace: true });
    return null;
  }

  const safeRedirect = parseSafeRedirectUri(redirectUri);
  const redirectHost = safeRedirect?.host ?? null;
  const redirectHostRegistered = !!client && !!redirectHost && client.redirect_hosts.includes(redirectHost);

  if (!clientId || !safeRedirect || clientError || (client && !redirectHostRegistered)) {
    return (
      <div className={styles.page}>
        <div className={styles.card}>
          <h1 className={styles.title}>Invalid authorization request</h1>
          <p className={styles.subtitle}>
            This link is invalid or incomplete. Start the connection again
            from the application you are trying to connect.
          </p>
        </div>
      </div>
    );
  }

  if (!client) {
    return (
      <div className={styles.page}>
        <div className={styles.card}>
          <p className={styles.subtitle}>Loading…</p>
        </div>
      </div>
    );
  }

  const clientName = client.client_name || 'Unnamed application';
  // Verification is decided server-side (VERIFIED_REDIRECT_HOSTS in
  // api/services/oauthClientService.js); anything but an explicit true warns.
  const verified = client.verified === true;

  const handleApprove = async () => {
    setSubmitting(true);
    setError(null);
    try {
      const result = await apiFetch('/oauth/consent/approve', {
        method: 'POST',
        body: {
          client_id: clientId,
          redirect_uri: redirectUri,
          code_challenge: searchParams.get('code_challenge'),
          code_challenge_method: searchParams.get('code_challenge_method'),
          state: searchParams.get('state') || undefined,
          scope: searchParams.get('scope') || undefined,
          resource: searchParams.get('resource') || undefined,
        },
      });
      window.location.assign(result.redirectUrl);
    } catch (err) {
      setError(err.message || 'Failed to approve the connection');
      setSubmitting(false);
    }
  };

  // safeRedirect is https or loopback http (checked above), never
  // javascript: — this is the only place the page navigates to a URL taken
  // from its own query string.
  const handleDeny = () => {
    const url = new URL(safeRedirect);
    url.searchParams.set('error', 'access_denied');
    const state = searchParams.get('state');
    if (state) url.searchParams.set('state', state);
    window.location.assign(url.toString());
  };

  return (
    <div className={styles.page}>
      <div className={styles.card}>
        <div className={verified ? styles.iconWrap : styles.iconWrapWarning}>
          {verified ? <ShieldCheck size={32} /> : <AlertTriangle size={32} />}
        </div>
        <h1 className={styles.title}>Connect {clientName}?</h1>
        <p className={styles.subtitle}>
          <strong>{clientName}</strong> is requesting access to your Vennio
          account{user?.email ? ` (${user.email})` : ''}.
        </p>

        <div className={styles.redirectHost}>
          <span className={styles.redirectHostLabel}>Access will be sent to</span>
          <span className={styles.redirectHostValue}>{redirectHost}</span>
        </div>

        {!verified && (
          <div className={styles.unverified} role="alert">
            <strong>Unverified app.</strong> Vennio has not verified this
            application. Its name is chosen by whoever registered it. Only
            approve if you started this connection yourself and
            trust <strong>{redirectHost}</strong>.
          </div>
        )}

        <ul className={styles.scopeList}>
          {scopes.map((scope) => (
            <li key={scope} className={styles.scopeItem}>
              {SCOPE_DESCRIPTIONS[scope]}
            </li>
          ))}
        </ul>

        {error && <div className={styles.error}>{error}</div>}

        <div className={styles.actions}>
          <button
            type="button"
            className={styles.denyButton}
            onClick={handleDeny}
            disabled={submitting}
          >
            <X size={16} /> Deny
          </button>
          <button
            type="button"
            className={styles.approveButton}
            onClick={handleApprove}
            disabled={submitting}
          >
            {submitting ? 'Connecting…' : 'Approve'}
          </button>
        </div>

        <p className={styles.footnote}>
          You can disconnect this application at any time from your developer
          settings.
        </p>
      </div>
    </div>
  );
}
